import AuthMixin from '@/components/mixins/auth-mixin'
import EventBus from '@/components/mixins/event-bus-mixin'
import UserAddress from '@/components/common/user-address/user-address-tmpl'
import UserAccount from './user-account/user-account-tmpl'
import AccountInformation from './account-information/account-information-tmpl'
import UserInvoiceList from './user-invoice-list/user-invoice-list-tmpl'
import UserInvoiceDetails from './user-invoice-details/user-invoice-details-tmpl'
import EditAccountNameDialog from './edit-account-name-dialog/edit-account-name-dialog-tmpl'
import { useMixpanel } from '@base-shared/composables/use-mixpanel'
import { useUserStore } from '@base-shared/stores/user';
import { useGroupStore } from '@base-shared/stores/group';

export default {
  name: 'userProfile',
  inject: [
    '$currentUser',
    '$utils',
    '$api',
    '$apiError',
    '$signedInUserDetails',
    '$signedInUserInfo',
    '$signedInUserAccounts'
  ],
  components: {
    EditAccountNameDialog,
    UserAddress,
    UserAccount,
    AccountInformation,
    UserInvoiceList,
    UserInvoiceDetails
  },
  watch: {
    '$route.params' (newVal) {
      this.setCenterPanel()
    },
    /**
     * Watches the `userStore.currentUser` for changes. Replacement
     * for the old `USER_DETAILS_AVAILABLE_IN_VUE` from EventBus.
     */
    'userStore.currentUser': {
      handler (newValue) {
        this.setDefaultData()
      },
      deep: true
    },
    /**
     * Watches the `userStore.accounts` for changes. Replacement
     * for the old `USER_ACCOUNTS_AVAILABLE_IN_VUE` from EventBus.
     */
    'userStore.accounts': {
      handler (newValue) {
        this.setDefaultData()
      },
      deep: true
    },
    /**
     * Watches the `groupStore.groupInfo` for changes. Replacement
     * for the old `BIND_USER_INFO_IN_VUE`, `USER_INFO_AVAILABLE_IN_VUE`
     * from EventBus.
     */
    'groupStore.groupInfo': {
      handler (newValue) {
        // Handle suspended active plan from group info
        this.isActivePlanSuspended = !!this.groupStore.groupInfo?.activePlan?.suspended
        this.setDefaultData()
      },
      deep: true
    }
  },
  mixins: [AuthMixin],
  data () {
    return {
      profileColumnHeaderIconClass: { icon: true },
      centerPanel: null,
      userInfo: this.$signedInUserInfo,
      userDetails: this.$signedInUserDetails,
      profileMenu: {
        account: 'account',
        address: 'address',
        customerInfo: 'customerInfo',
        billingDetails: 'billingDetails',
        invoices: 'invoices',
        leaveAccount: 'leaveAccount',
        invoiceDetails: 'invoiceDetails',
        settings: 'settings',
        userManagement: 'userManagement'
      },
      visibleProfileMenu: [],
      deletedUserNotification: false,
      userAccounts: [],
      dialog: {
        editAccountName: 'editAccountName',
        deleteAccount: 'deleteAccount'
      },
      activeDialog: null,
      isActivePlanSuspended: false
    }
  },
  setup () {
    // Expose the user store to the Options API context.
    return {
      userStore: useUserStore(),
      groupStore: useGroupStore()
    };
  },
  methods: {
    getRouteCenterPanelVal () {
      let routeCenterPanelVal = null

      if (this.$route.params && this.$route.params.centerPanel) {
        routeCenterPanelVal = this.$utils.dashToCamelcase(this.$route.params.centerPanel)

        if (routeCenterPanelVal === 'invoices' && this.$route.params.id) {
          routeCenterPanelVal = this.profileMenu.invoiceDetails
        }
      }

      return routeCenterPanelVal
    },
    setDefaultData () {
      this.userDetails = this.$signedInUserDetails
      this.userInfo = this.$signedInUserInfo
      this.userAccounts = this.$signedInUserAccounts

      if ((this.userDetails && this.userInfo) && (this.userInfo.address.firstName === '' && this.userInfo.address.lastName === '')) {
        const userInfo = this.$utils.clone(this.userInfo)
        userInfo.address.firstName = this.userDetails.firstName
        userInfo.address.lastName = this.userDetails.lastName
        this.userInfo = userInfo
      }

      this.visibleProfileMenu = [this.profileMenu.account]

      // Settings option for users with Admin and Billing rights
      if (
        !this.$currentUser.isStandAloneUser() &&
        !this.$currentUser.isFreeUser() &&
        (this.$currentUser.hasGroupUserAdmin() || this.$currentUser.hasGroupUserBilling()) &&
        !this.isActivePlanSuspended
      ) {
        this.visibleProfileMenu.push(this.profileMenu.billingDetails)
        this.visibleProfileMenu.push(this.profileMenu.invoices)
        this.visibleProfileMenu.push(this.profileMenu.invoiceDetails)
      }

      // Add this option for users with Admin rights
      if (
        !this.$currentUser.isStandAloneUser() &&
        !this.$currentUser.isFreeUser() &&
        this.$currentUser.hasGroupUserAdmin()
      ) {
        this.visibleProfileMenu.push(this.profileMenu.userManagement)
      }

      // Only visible menu item for StandAlone User
      if (this.$currentUser.isStandAloneUser() || this.$currentUser.isFreeUser()) {
        this.visibleProfileMenu.push(this.profileMenu.customerInfo)
      }

      // Settings option for users with Licenses rights
      if (this.$utils.isPageForLicenses()) {
        this.visibleProfileMenu = [this.profileMenu.account]
        this.visibleProfileMenu.push(this.profileMenu.customerInfo)
      }

      // Settings option for users with enterprise rights
      if (this.$currentUser.hasGroupEnterprise()) {
        this.visibleProfileMenu = [this.profileMenu.account]

        if (this.$currentUser.hasGroupUserAdmin() || this.$currentUser.hasGroupUserBilling()) {
          this.visibleProfileMenu.push(this.profileMenu.customerInfo)
        }

        if (this.$currentUser.hasGroupUserAdmin() && !this.$utils.isPageForLicenses()) {
          this.visibleProfileMenu.push(this.profileMenu.userManagement)
        }
      }
    },
    setCenterPanel () {
      this.centerPanel = null
      const centerPanelVal = this.getRouteCenterPanelVal()

      if (this.visibleProfileMenu.indexOf(centerPanelVal) < 0) return

      // Center panel is not invoice Details
      if (centerPanelVal !== this.profileMenu.invoiceDetails) {
        this.userInfo = this.$signedInUserInfo
        this.centerPanel = centerPanelVal
      }

      // Center panel is an invoice Details
      if (centerPanelVal === this.profileMenu.invoiceDetails) {
        this.centerPanel = centerPanelVal
      }
    },
    handleProfileMenuItemClick (menuItem) {
      if (menuItem === this.profileMenu.leaveAccount) {
        this.handleLeaveAccount()
        return
      }

      // Redirect to User's Management page
      if (menuItem === this.profileMenu.userManagement) {
        this.$router.push('/user-admin', () => {})
        return
      }

      // Update route to change right content
      useMixpanel().track('Settings - ' + menuItem)
      const dashRouteItem = this.$utils.camelcaseToDash(menuItem)
      this.$router.push(`/settings/${dashRouteItem}`, () => {})
    },
    /**
     * Hook function when user address component change
     * @param {Object} event
     */
    handleUserAddressSubCompChange (event) {
      if (event.action === 'USERS_INFO_ADDRESS_UPDATED') {
        const userInfo = this.$utils.clone(this.userInfo)
        userInfo.address = event.payload.data

        this.userInfo = userInfo
        $('.center').removeClass('fadeInRight').addClass('fadeOutRight')
        // Refresh user accounts latest account name
        this.getUserAccountsFromBackend()

        // Add set timout for panel slide animation
        window.setTimeout(() => {
          this.$router.push('/settings', () => {})
        }, 600)
      } else if (event.action === 'EDIT_ACCOUNT_NAME') {
        this.activeDialog = this.dialog.editAccountName
      } else if (event.action === 'DELETE_ACCOUNT') {
        this.activeDialog = this.dialog.editAccountName
      }
    },
    handleLeaveAccount () {
      useMixpanel().track('Leave Account')

      const leaveAccountRequest = () => {
        this.$utils.mainLoader(true)
        this.$api.delete(`${CONFIG.API_BASE_URL}/group/users/me`)
          .then(response => {
            const self = this
            const switchAccountRequest = () => {
              self.$utils.mainLoader(true)

              const currentGroupId = self.$currentUser.getUserGroupId()
              const getCurrentAccount = self.$signedInUserAccounts.find(account => account.groupId === currentGroupId)
              const currentAccountName = (getCurrentAccount && getCurrentAccount.name) ? getCurrentAccount.name : null
              const getFirstItemFromAccounts = self.$signedInUserAccounts.find(account => account.groupId !== currentGroupId)
              const switchToAccountGroupId = (getFirstItemFromAccounts && getFirstItemFromAccounts.groupId) ? getFirstItemFromAccounts.groupId : null

              self.$api.put(`${CONFIG.API_BASE_URL}/group/accounts/${switchToAccountGroupId}`)
                .then(response => {
                  self.refreshUserSession({
                    keepLoaderVisible: true,
                    nextAction: () => {
                    // Redirect to Left Account page
                      const encodedAccountName = encodeURIComponent(currentAccountName)
                      self.$router.push(`/left-account/${encodedAccountName}`)
                      self.$utils.mainLoader(false)
                    }
                  })
                })
                .catch((error) => {
                  self.$apiError.handleAPIRequestError(error, { originalRequest: () => { switchAccountRequest() }, showDialogNotification: true })
                  self.$utils.mainLoader(false)
                })
            }

            // Auto switch to another account if more than one account
            if (self.$signedInUserAccounts.length > 1) {
              switchAccountRequest()
            } else {
              self.signOut()
              window.setTimeout(() => {
                self.$utils.mainLoader(false)
              }, 1000)
            }
          })
          .catch((error) => {
            this.$utils.mainLoader(false)
            this.$apiError.handleAPIRequestError(error, {
              originalRequest: () => { leaveAccountRequest() },
              showDialogNotification: true,
              dialogErrorMessage: error.response.data?.message ?? ''
            })
          })
      }

      let message = ''
      let descriptionI18n = null
      let callbacks = {}
      const confirmButtonText = this.$t('label.leaveAccount')
      const confirmButtonIcon = 'flowbite:trash-bin-solid'
      const leaveAccount = {
        email: this.userDetails.email,
        accountName: this.userInfo?.accountName ?? this.userInfo?.address.companyName ?? null
      }

      if (!this.userInfo.hasMultipleUsers) {
        message = this.$t('label.leaveAccountWithOnlyOneUserMessage')
        callbacks = {
          eventOnClose: () => {},
        }
      } else {
        descriptionI18n = { key: 'leaveAccount.leaveAccountConfirmation', params: { accountName: leaveAccount.accountName } }
        message = this.$t('leaveAccount.leaveAccountWarning')
        callbacks = {
          eventOKButtonClick: () => leaveAccountRequest(),
        }
      }

      // Send global event to show custom dialog
      EventBus.$emit('SHOW_CUSTOM_NOTIFICATION', {
        type: 'warning',
        title: this.$t('confirm.leaveAccount'),
        descriptionI18n,
        alert: {
          message
        },
        confirmButtonText,
        confirmButtonIcon,
        callbacks
      })
    },
    handleSwitchAccountCompChange (event) {
      if (event.action === 'NEW_USER_INFO') {
        this.userInfo = event.payload.data.userInfo
      }
    },
    handleUserSettingsCompChange (event) {
      if (event.action === 'NEW_USER_INFO' && event.payload.data.userInfo) {
        this.userInfo = event.payload.data.userInfo
      }
    },
    handleAccountInfoEvent (event) {
      if (event.action !== 'CLOSE_EDIT_ACCOUNT_NAME_DIALOG') {
        return;
      }

      this.activeDialog = null

      if (event?.payload?.data?.accountName) {
        const userInfo = this.$utils.clone(this.userInfo)
        userInfo.accountName = event.payload.data.accountName
        this.userInfo = userInfo
        // Refresh user accounts latest account name
        this.getUserAccountsFromBackend()
      }
    }
  },
  mounted () {
    useMixpanel().track('Profile')
    // Handle suspended active plan from userInfo
    this.isActivePlanSuspended = !!this.$signedInUserInfo?.activePlan?.suspended

    this.isAuthenticated({
      callback: () => {
        this.setDefaultData()
        this.setCenterPanel()
        this.getUserUserInfoFromBackend()
        this.getUserAccountsFromBackend()
      }
    })

    EventBus.$on('SIGNED_IN', (payLoad) => {
      this.setDefaultData()
    })
  }
}
